import { Component, computed, effect, inject, signal } from '@angular/core';
import { MessageService } from 'primeng/api';

type ErrorFile = { file: File; message: string };
type UploadStatus = {
  done: number;
  total: number;
  erroredFiles: ErrorFile[];
};
const uploadProgressStatus = signal<UploadStatus | null>(null);
const defaultUploadStatus: UploadStatus = {
  total: 0,
  done: 0,
  erroredFiles: []
};

export const injectUploadProgressStatus = () => {
  const messageService = inject(MessageService);

  const percentage = computed(() => {
    const currentStatus = uploadProgressStatus();
    if (currentStatus === null || currentStatus.total <= 0) {
      return 0;
    }

    return Math.floor((currentStatus.done / currentStatus.total) * 100);
  });

  const createUploadProgressStatus = (total: number) => {
    uploadProgressStatus.set({ ...defaultUploadStatus, total });

    messageService.add({
      key: 'file-upload-status',
      sticky: true,
      severity: 'custom',
      summary: 'Prenosimo vaše datoteke.'
    });
  };

  const updateTotal = (total: number) => {
    const safeCurr = uploadProgressStatus() ?? {
      ...defaultUploadStatus,
      total
    };
    safeCurr.total = total;

    uploadProgressStatus.set({ ...safeCurr });
  };

  const addErrorFile = (file: File, message: string) => {
    const safeCurr = uploadProgressStatus();
    if (!safeCurr) {
      throw new Error('Cannot update done when status is null');
    }

    safeCurr.erroredFiles = [...safeCurr.erroredFiles, { file, message }];
    return safeCurr;
  };

  const incrementDone = () => {
    const safeCurr = uploadProgressStatus();
    if (!safeCurr) {
      throw new Error('Cannot update done when status is null');
    }

    uploadProgressStatus.set({ ...safeCurr, done: safeCurr.done + 1 });
  };

  const updateDone = (done: number) => {
    const safeCurr = uploadProgressStatus();
    if (!safeCurr) {
      throw new Error('Cannot update done when status is null');
    }

    safeCurr.done = done;

    uploadProgressStatus.set({ ...safeCurr });
  };

  const hide = () => {
    messageService.clear('file-upload-status');
  };

  const clear = () => {
    messageService.clear('file-upload-status');
    uploadProgressStatus.set(null);
  };

  return {
    uploadProgressStatus,
    addErrorFile,
    percentage,
    createUploadProgressStatus,
    updateTotal,
    updateDone,
    incrementDone,
    clear,
    hide
  };
};

@Component({
  selector: 'app-file-upload-progress-toast',
  styles: [
    `
      div.toast-container {
        padding: 0 !important;
        margin: 0 !important;
      }
      div.p-toast-message-content {
        background: white !important;
        opacity: 200 !important;
      }
    `
  ],
  template: `
    <div class="card bg-white flex justify-content-center toast-container">
      <p-toast
        position="bottom-center"
        key="file-upload-status"
        [baseZIndex]="5000"
      >
        <ng-template let-message pTemplate="headless" let-closeFn="closeFn">
          <div class="bg-white p-3">
            <div class="flex flex-column bg-white gap-3 w-full">
              <div class="flex justify-content-between">
                <i class="pi pi-cloud-upload text-black text-2xl"></i>
                <p-button
                  icon="pi pi-times"
                  [rounded]="true"
                  [text]="true"
                  severity="secondary"
                  (onClick)="close()"
                />
              </div>
              <p class="m-0 font-semibold text-base text-black">
                {{ message.summary }}
              </p>
              <p class="m-0 text-base text-700">
                {{ message.detail }}
              </p>
              <div class="flex flex-column gap-2">
                <p-progressBar
                  [value]="progress.percentage()"
                  [showValue]="false"
                  [style]="{ height: '4px' }"
                />
                <label class="text-right text-xs text-black">
                  Preneseno {{ progress.percentage() }}% datoteka...
                </label>
              </div>
            </div>
          </div>
        </ng-template>
      </p-toast>
    </div>
  `
})
export class FileUploadProgressToastComponent {
  public progress = injectUploadProgressStatus();

  private messageService = inject(MessageService);

  constructor() {
    effect(() => {
      if (this.progress.uploadProgressStatus() == null) {
        this.messageService.clear('file-upload-status');
      }
    });

    effect(() => {
      const currentStatus = this.progress.uploadProgressStatus();
      if (
        currentStatus &&
        currentStatus.total > 0 &&
        this.progress.percentage() === 100
      ) {
        const timeout = setTimeout(() => {
          this.progress.clear();
        }, 10_000);

        return () => {
          clearTimeout(timeout);
        };
      }
      return () => {};
    });
  }

  public close() {
    this.progress.hide();
  }
}
